import { useEffect, useRef, useState } from "react";
import ComplianceNotice from "@/components/ComplianceNotice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ThinkingIndicator } from "@/components/ThinkingIndicator";
import {
  Sparkles,
  Image as ImageIcon,
  X,
  Copy,
  RotateCw,
  Facebook,
  Instagram,
  Megaphone,
  Wand2,
  Check,
  ChevronDown,
  Bookmark,
  BookmarkCheck,
} from "lucide-react";

const PLATFORM_META = {
  facebook: { label: "Facebook", icon: Facebook, hint: "Long-form" },
  x: { label: "X", icon: Megaphone, hint: "≤ 280 chars" },
  instagram: { label: "Instagram", icon: Instagram, hint: "Caption" },
};

const QUICK_INSTRUCTIONS = [
  "Make it shorter",
  "More aggressive",
  "More folksy",
  "Less formal",
  "Add a clear call to action",
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const idx = String(result).indexOf("base64,");
      resolve(idx >= 0 ? String(result).slice(idx + 7) : "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function SocialGenerator() {
  const location = useLocation();
  const navigate = useNavigate();
  const [moment, setMoment] = useState("");
  const [personalFacts, setPersonalFacts] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [imageMime, setImageMime] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [historyId, setHistoryId] = useState(null);
  const [savedPlatforms, setSavedPlatforms] = useState({}); // { facebook: true, ... }
  const [savingPlatform, setSavingPlatform] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [profileReady, setProfileReady] = useState(null);
  const [prefillBanner, setPrefillBanner] = useState("");
  const fileRef = useRef(null);

  // Refine state — keyed by draft index
  const [editingIdx, setEditingIdx] = useState(null);
  const [instructions, setInstructions] = useState({});
  const [refining, setRefining] = useState({});
  const [revisions, setRevisions] = useState({}); // idx -> { original, revised }

  useEffect(() => {
    const state = location.state;
    if (state && typeof state.prefill === "string" && state.prefill.trim()) {
      setMoment(state.prefill);
      if (state.source === "calendar") {
        setPrefillBanner("Pre-loaded from your content calendar. Edit anything below before generating.");
      }
      // Clear the state so a manual refresh doesn't re-prefill
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  useEffect(() => {
    api.get("/campaign").then(({ data }) => {
      const isCommittee = data?.account_type === "committee";
      const primary = isCommittee ? data?.committee_name : data?.candidate_name;
      const secondary = isCommittee ? data?.party : data?.office_sought;
      setProfileReady(!!(data && primary && secondary));
    }).catch(() => setProfileReady(false));
    // Prefill from campaign profile once on mount. Setters are stable.
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please use JPG, PNG, or WEBP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB.");
      return;
    }
    const b64 = await fileToBase64(file);
    setImageBase64(b64);
    setImageMime(file.type);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageBase64("");
    setImageMime("");
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const resetRefineState = () => {
    setEditingIdx(null);
    setInstructions({});
    setRefining({});
    setRevisions({});
  };

  const generate = async () => {
    if (!moment.trim()) {
      toast.error("Tell us about the moment first.");
      return;
    }
    setLoading(true);
    setError("");
    resetRefineState();
    try {
      const payload = { moment: moment.trim() };
      if (personalFacts.trim()) {
        payload.personal_facts = personalFacts.trim();
      }
      if (imageBase64) {
        payload.image_base64 = imageBase64;
        payload.image_mime = imageMime || "image/jpeg";
      }
      const { data } = await api.post("/posts/generate", payload);
      setDrafts(data.drafts || []);
      setHistoryId(data.id || null);
      setSavedPlatforms({});
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Select the text manually.");
    }
  };

  const toggleSave = async (platform) => {
    if (!historyId) return;
    const wasSaved = !!savedPlatforms[platform];
    setSavingPlatform(platform);
    try {
      // Persist the current (possibly user-edited) text along with the save toggle
      // so the saved copy matches what the user sees on screen.
      const draft = drafts.find((x) => x.platform === platform);
      const payload = { saved: !wasSaved };
      if (!wasSaved && draft) payload.text = draft.text;
      await api.patch(`/posts/history/${historyId}/drafts/${platform}`, payload);
      setSavedPlatforms((s) => ({ ...s, [platform]: !wasSaved }));
      toast.success(wasSaved ? "Removed from Saved Posts" : "Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPlatform(null);
    }
  };

  const updateDraftText = (i, text) => {
    setDrafts((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], text };
      return next;
    });
  };

  const submitRefine = async (i) => {
    const instr = (instructions[i] || "").trim();
    if (!instr) {
      toast.error("Tell us what to change.");
      return;
    }
    const d = drafts[i];
    setRefining((s) => ({ ...s, [i]: true }));
    setRevisions((r) => {
      const n = { ...r };
      delete n[i];
      return n;
    });
    try {
      const { data } = await api.post("/posts/refine", {
        original: d.text,
        instruction: instr,
        platform: d.platform,
      });
      setRevisions((r) => ({ ...r, [i]: { original: d.text, revised: data.revised } }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRefining((s) => ({ ...s, [i]: false }));
    }
  };

  const acceptEdit = (i) => {
    const rev = revisions[i];
    if (!rev) return;
    updateDraftText(i, rev.revised);
    setRevisions((r) => {
      const n = { ...r };
      delete n[i];
      return n;
    });
    setInstructions((s) => ({ ...s, [i]: "" }));
    setEditingIdx(null);
    toast.success("Edit accepted");
  };

  const refineAgain = (i) => {
    const rev = revisions[i];
    if (!rev) return;
    // Promote revised to the new baseline and keep the panel open
    updateDraftText(i, rev.revised);
    setRevisions((r) => {
      const n = { ...r };
      delete n[i];
      return n;
    });
    setInstructions((s) => ({ ...s, [i]: "" }));
  };

  const discardEdit = (i) => {
    setRevisions((r) => {
      const n = { ...r };
      delete n[i];
      return n;
    });
  };

  const closeEditPanel = (i) => {
    discardEdit(i);
    setInstructions((s) => ({ ...s, [i]: "" }));
    setEditingIdx((cur) => (cur === i ? null : cur));
  };

  if (profileReady === false) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="bg-white border border-slate-200 rounded-xl p-10 shadow-sm">
          <h1 className="font-display text-2xl font-bold text-flag-navy">Finish your campaign intake first</h1>
          <p className="text-slate-600 mt-2">We use your saved profile to ground every draft.</p>
          <Link to="/intake" data-testid="generator-no-profile-cta">
            <Button className="mt-6 bg-flag-red hover:bg-flag-red-hover text-white font-bold h-11 px-5">
              Go to intake
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Social Post Generator</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">Draft a post in seconds</h1>
        <p className="text-slate-600 mt-2">
          Describe a moment from the trail. We'll write a Facebook post and two short drafts for X & Instagram — in your voice.
        </p>
      </div>

      <ComplianceNotice kind="ai" className="mt-4" />

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8">
          <Label className="text-sm font-semibold text-slate-800">The moment</Label>
          {prefillBanner && (
            <div className="mt-2 mb-1 text-xs font-semibold text-flag-navy bg-flag-navy/5 border border-flag-navy/15 rounded-md px-2.5 py-1.5" data-testid="generator-prefill-banner">
              {prefillBanner}
            </div>
          )}
          <Textarea
            value={moment}
            onChange={(e) => setMoment(e.target.value)}
            rows={7}
            placeholder='e.g., "I marched in the Fourth of July parade in Canandaigua. Met a small business owner who said her property taxes went up 30% in three years."'
            className="mt-2 text-base"
            data-testid="generator-moment-input"
          />

          <div className="mt-6">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <Label htmlFor="generator-personal-facts-input" className="text-sm font-semibold text-slate-800">
                Personal facts to include <span className="font-normal text-slate-500">(optional)</span>
              </Label>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                Unlocks first-person specifics
              </span>
            </div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Drop 1&ndash;3 personal details that are <strong>actually true</strong> and you&rsquo;re happy for the AI to use — e.g. a family member&rsquo;s name, a specific date, a real story, a local place. The AI will use these verbatim in the draft&rsquo;s first-person voice. If you leave this blank, the AI will stay general or insert a <code className="bg-slate-100 px-1 rounded">[ADD YOUR MEMORY]</code> placeholder rather than invent anything.
            </p>
            <div className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
              <div className="font-semibold text-slate-600">Examples of what to write:</div>
              <ul className="list-disc list-inside space-y-0.5 mt-0.5">
                <li>My grandfather Frank served in Korea and is buried at St. Mary&rsquo;s in Hannibal.</li>
                <li>My daughter Emma&rsquo;s birthday is tomorrow — she just turned 12.</li>
                <li>My father-in-law was a volunteer firefighter in Fulton for 30 years.</li>
              </ul>
            </div>
            <Textarea
              id="generator-personal-facts-input"
              value={personalFacts}
              onChange={(e) => setPersonalFacts(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="One fact per line, or a short paragraph. Only include things you know are true."
              className="mt-2 text-sm"
              data-testid="generator-personal-facts-input"
            />
          </div>

          <div className="mt-6">
            <Label className="text-sm font-semibold text-slate-800">Photo (optional)</Label>
            <p className="text-sm text-slate-500 mt-1">JPG, PNG, or WEBP. The Facebook draft will draw on the photo's mood and setting.</p>
            <div className="mt-3">
              {imagePreview ? (
                <div className="relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Selected"
                    className="rounded-lg border border-slate-200 max-h-48"
                    data-testid="generator-image-preview"
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    data-testid="generator-image-remove"
                    className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-flag-navy text-white flex items-center justify-center shadow"
                    aria-label="Remove photo"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="image-upload"
                  data-testid="generator-image-upload-label"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-flag-navy rounded-lg px-4 py-8 cursor-pointer transition-colors"
                >
                  <ImageIcon className="h-6 w-6 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-700">Click to upload a photo</span>
                  <span className="text-xs text-slate-500">JPG · PNG · WEBP · up to 8MB</span>
                  <input
                    id="image-upload"
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={onPick}
                    className="hidden"
                    data-testid="generator-image-upload-input"
                  />
                </label>
              )}
            </div>
          </div>

          <Button
            onClick={generate}
            disabled={loading || !moment.trim()}
            data-testid="generator-generate-btn"
            className="mt-6 w-full h-12 bg-flag-red hover:bg-flag-red-hover text-white font-bold text-base"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {loading ? "Drafting…" : drafts.length > 0 ? "Regenerate drafts" : "Generate posts"}
          </Button>

          {error && (
            <div className="mt-4 text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="generator-error">
              {error}
            </div>
          )}

          <p className="mt-4 text-xs text-slate-500">
            Tip: paste any existing post into a draft below and click <span className="font-semibold">Suggest edit</span> to revise it in plain English.
          </p>
        </div>

        {/* Output */}
        <div className="space-y-4">
          {drafts.length === 0 && !loading && (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="generator-empty">
              Your drafts will appear here.
            </div>
          )}
          {loading && (
            <ThinkingIndicator
              label="Drafting three versions in your voice"
              testid="generator-loading"
            />
          )}
          {drafts.length > 0 && (
            <div
              className="bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3 flex items-start gap-2.5"
              data-testid="generator-review-notice"
              role="note"
            >
              <span aria-hidden="true" className="text-amber-600 text-lg leading-none mt-0.5">⚠</span>
              <p className="text-xs text-amber-900 leading-snug">
                <strong>Review before posting.</strong> Read each draft and verify every personal detail, name, date, location, and statistic is accurate. Any bracketed placeholders like <code className="bg-amber-100 px-1 rounded">[ADD YOUR PERSONAL MEMORY HERE]</code> must be filled in by you — the AI intentionally does not invent first-person experiences it wasn&rsquo;t told.
              </p>
            </div>
          )}
          {drafts.map((d, i) => {
            const meta = PLATFORM_META[d.platform] || PLATFORM_META.facebook;
            const Icon = meta.icon;
            const isEditing = editingIdx === i;
            const isRefining = !!refining[i];
            const rev = revisions[i];
            return (
              <div
                key={d.platform}
                className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 animate-fade-up"
                data-testid={`generator-draft-${d.platform}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-flag-navy/5 text-flag-navy">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="font-display font-bold text-flag-navy">{meta.label}</div>
                      <div className="text-xs text-slate-500">{meta.hint}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={savedPlatforms[d.platform] ? "secondary" : "outline"}
                      onClick={() => toggleSave(d.platform)}
                      disabled={!historyId || savingPlatform === d.platform}
                      data-testid={`generator-save-${d.platform}`}
                      className={`h-9 font-semibold ${savedPlatforms[d.platform] ? "bg-flag-red text-white hover:bg-flag-red-hover" : "border-2"}`}
                    >
                      {savedPlatforms[d.platform] ? (
                        <>
                          <BookmarkCheck className="h-3.5 w-3.5 mr-1.5" /> Saved
                        </>
                      ) : (
                        <>
                          <Bookmark className="h-3.5 w-3.5 mr-1.5" />
                          {savingPlatform === d.platform ? "Saving…" : "Save"}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => copy(d.text)}
                      data-testid={`generator-copy-${d.platform}`}
                      className="h-9 border-2 font-semibold"
                    >
                      <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                    </Button>
                    <Button
                      variant={isEditing ? "secondary" : "outline"}
                      onClick={() => (isEditing ? closeEditPanel(i) : setEditingIdx(i))}
                      data-testid={`generator-edit-toggle-${d.platform}`}
                      className={`h-9 font-semibold ${isEditing ? "bg-flag-navy text-white hover:bg-flag-navy-2" : "border-2"}`}
                    >
                      <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                      {isEditing ? "Close" : "Suggest edit"}
                      {!isEditing && <ChevronDown className="h-3.5 w-3.5 ml-1" />}
                    </Button>
                  </div>
                </div>

                <Textarea
                  value={d.text}
                  onChange={(e) => updateDraftText(i, e.target.value)}
                  rows={d.platform === "facebook" ? 7 : 3}
                  className="mt-4 text-base leading-relaxed"
                  data-testid={`generator-draft-text-${d.platform}`}
                />

                {isEditing && (
                  <div
                    className="mt-4 border-t border-slate-100 pt-4 space-y-3 animate-fade-up"
                    data-testid={`generator-edit-panel-${d.platform}`}
                  >
                    <Label className="text-sm font-semibold text-slate-800">What should change?</Label>
                    <Textarea
                      rows={2}
                      value={instructions[i] || ""}
                      onChange={(e) =>
                        setInstructions((s) => ({ ...s, [i]: e.target.value }))
                      }
                      placeholder={`e.g. "Make it shorter and mention the parade"`}
                      className="text-base"
                      data-testid={`generator-edit-instruction-${d.platform}`}
                    />
                    <div className="flex flex-wrap gap-2">
                      {QUICK_INSTRUCTIONS.map((q) => (
                        <button
                          key={q}
                          type="button"
                          onClick={() =>
                            setInstructions((s) => ({ ...s, [i]: q }))
                          }
                          data-testid={`generator-edit-chip-${d.platform}-${q.replace(/\s+/g, "-").toLowerCase()}`}
                          className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        onClick={() => submitRefine(i)}
                        disabled={isRefining || !(instructions[i] || "").trim()}
                        data-testid={`generator-edit-submit-${d.platform}`}
                        className="h-10 px-4 bg-flag-navy hover:bg-flag-navy-2 text-white font-bold"
                      >
                        <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                        {isRefining ? "Revising…" : rev ? "Re-run" : "Suggest edit"}
                      </Button>
                      {rev && (
                        <span className="text-xs text-slate-500">Compare below, then accept or refine again.</span>
                      )}
                    </div>

                    {rev && (
                      <div
                        className="mt-3 grid sm:grid-cols-2 gap-3 animate-fade-up"
                        data-testid={`generator-edit-compare-${d.platform}`}
                      >
                        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Original</div>
                          <p
                            className="mt-2 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed"
                            data-testid={`generator-edit-original-${d.platform}`}
                          >
                            {rev.original}
                          </p>
                        </div>
                        <div className="border-2 border-flag-red rounded-lg p-3 bg-red-50/40">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-flag-red">Proposed edit</div>
                          <p
                            className="mt-2 text-sm text-slate-900 whitespace-pre-wrap leading-relaxed"
                            data-testid={`generator-edit-revised-${d.platform}`}
                          >
                            {rev.revised}
                          </p>
                        </div>
                        <div className="sm:col-span-2 flex flex-wrap items-center gap-2 pt-1">
                          <Button
                            onClick={() => acceptEdit(i)}
                            data-testid={`generator-edit-accept-${d.platform}`}
                            className="h-10 px-4 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
                          >
                            <Check className="h-4 w-4 mr-1.5" /> Accept edit
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => refineAgain(i)}
                            data-testid={`generator-edit-keep-refining-${d.platform}`}
                            className="h-10 px-4 border-2 font-semibold"
                          >
                            <RotateCw className="h-4 w-4 mr-1.5" /> Keep refining
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => discardEdit(i)}
                            data-testid={`generator-edit-discard-${d.platform}`}
                            className="h-10 px-3 font-semibold text-slate-600 hover:text-flag-red"
                          >
                            Discard
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {drafts.length > 0 && (
            <Button
              variant="outline"
              onClick={generate}
              disabled={loading}
              data-testid="generator-regenerate-btn"
              className="w-full h-11 border-2 font-semibold"
            >
              <RotateCw className="h-4 w-4 mr-2" /> Regenerate
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
